import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { formatRelative } from "../utils/datetime";
import { WEBHOOK_EVENT_TYPES, Webhook, WebhookEventType, WebhookKind } from "../types";
import { useSort, sortRows, SortableTh } from "./SortableTh";

/**
 * Where alerts go: Slack, Teams, PagerDuty, or your own endpoint.
 *
 * REPORTED: "Adding slack / Teams seems to be confusing and difficult to
 * understand." It was, and the reason was the shape of this dialog rather
 * than any one label. It opened on a form asking for a "Kind" and a "URL", so
 * you had to already know that "I want alerts in Slack" means "make a
 * webhook", and then already know where a Slack webhook URL comes from. The
 * product said neither. Teams was worse - a bare `https://...` placeholder.
 *
 * So this is now DESTINATION FIRST, then the vendor's own steps, then a test:
 *
 *   1. pick Slack / Teams / PagerDuty / JSON
 *   2. follow the numbered steps, which name the exact menus to click, and
 *      paste the URL they produce - the shape is checked as you type
 *   3. SEND A TEST BEFORE SAVING, while the vendor page is still open
 *   4. name it and choose which events it gets
 *
 * The steps come from the server (/webhooks/destinations), not from here, so
 * the dialog, the Help guide and the integrations reference cannot disagree -
 * and correcting an instruction is one edit. See services/chat_notify_setup.py.
 */

/**
 * What the server sends instead of a stored URL when the signed-in user is
 * not an admin. A webhook URL is a bearer credential - a Slack one, or a
 * Power Automate one with its `sig=`, is enough on its own to post into the
 * customer's channel - so technicians can manage a destination without being
 * able to read the secret back out. Sending the placeholder back unchanged
 * means "leave it as it is"; see routers/webhooks.py.
 */
const MASKED_URL = "https://masked.invalid/";

const isMaskedUrl = (value: string) => value.trim().replace(/\/$/, "") === MASKED_URL.replace(/\/$/, "");

const KIND_LABELS: Record<WebhookKind, string> = {
  slack: "Slack",
  // Match the picker exactly - the saved-destinations table used to call the
  // Power Automate one "(workflow)" and the direct one plain "Microsoft
  // Teams", which is the reverse of what an operator should reach for.
  teams: "Microsoft Teams",
  teams_graph: "Microsoft Teams (direct)",
  pagerduty: "PagerDuty",
  generic: "Generic JSON",
};

const EVENT_LABELS: Record<WebhookEventType, string> = {
  "alert.critical": "Critical alert",
  "alert.warning": "Warning alert",
  "alert.info": "Info alert",
  "sla.response_breach": "SLA response breach",
  "sla.resolution_breach": "SLA resolution breach",
};

type Destination = {
  key: string;
  label: string;
  blurb: string;
  steps: string[];
  url_placeholder: string;
  url_shape: string;
  needs_key: boolean;
  key_label: string;
  key_help: string;
  docs_url: string;
  sample: string;
  notes: string[];
  fallback_title: string;
  fallback_steps: string[];
  open_links: { label: string; url: string }[];
  url_hosts: string[];
  url_must_contain: string[];
  no_url: boolean;
  recommended: boolean;
};

type Team = { id: string; name: string };
type Channel = { id: string; name: string };

type Signature = { pattern: string; belongs_to: string | null; message: string };

const EMPTY_DRAFT = {
  // teams_graph targets, null for every other kind.
  customer_id: null as number | null,
  target_team_id: "",
  target_channel_id: "",
  target_label: "",
  name: "",
  kind: "slack" as WebhookKind,
  url: "",
  integration_key: "",
  event_filter: ["alert.critical", "alert.warning"] as string[],
  enabled: true,
};

export default function WebhooksCard() {
  const { currentCustomerId } = useCustomer();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<typeof EMPTY_DRAFT>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [testResults, setTestResults] = useState<Record<number, string>>({});
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  // teams_graph: pick a customer, then a team, then a channel. No URL exists
  // for this kind - AeroGauge posts through the customer's own Microsoft 365.
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [graphErr, setGraphErr] = useState("");
  const [loadingTeams, setLoadingTeams] = useState(false);
  // null while the operator is still choosing where to send. Editing an
  // existing row jumps straight past the picker.
  const [picked, setPicked] = useState<string | null>(null);
  const [tryState, setTryState] = useState<{ busy: boolean; result: string; ok: boolean }>({
    busy: false,
    result: "",
    ok: false,
  });
  const whSort = useSort("name", "asc");

  const load = () => {
    api
      .get("/api/v1/webhooks")
      .then((r) => setWebhooks(r.data))
      .catch(() => setWebhooks([]));
  };
  useEffect(() => {
    load();
    api
      .get("/api/v1/webhooks/destinations")
      .then((r) => {
        setDestinations(r.data?.destinations || r.data || []);
        setSignatures(r.data?.signatures || []);
      })
      .catch(() => setDestinations([]));
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
  }, []);

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setEditing(null);
    setPicked(null);
    setTryState({ busy: false, result: "", ok: false });
    setCreating(true);
    setError("");
  };
  const openEdit = (w: Webhook) => {
    setDraft({
      customer_id: w.customer_id ?? null,
      target_team_id: w.target_team_id || "",
      target_channel_id: w.target_channel_id || "",
      target_label: w.target_label || "",
      name: w.name,
      kind: w.kind,
      url: w.url,
      integration_key: w.integration_key || "",
      event_filter: [...w.event_filter],
      enabled: w.enabled,
    });
    setEditing(w);
    setPicked(w.kind);
    setTryState({ busy: false, result: "", ok: false });
    setCreating(false);
    setError("");
  };
  const close = () => {
    setPicked(null);
    setTryState({ busy: false, result: "", ok: false });
    setCreating(false);
    setEditing(null);
    setError("");
  };

  const save = async () => {
    setError("");
    if (!draft.name.trim()) {
      setError("Name is required");
      return;
    }
    // teams_graph has NO URL - that is the entire point of it. This guard
    // predates that kind and refused to save a perfectly configured Teams
    // destination with "URL is required", after the operator had already
    // picked a team and a channel.
    if (draft.kind === "teams_graph") {
      if (!draft.customer_id) {
        setError("Pick the customer whose Microsoft 365 to post through.");
        return;
      }
      if (!draft.target_team_id || !draft.target_channel_id) {
        setError("Pick a team and a channel.");
        return;
      }
    } else if (!draft.url.trim()) {
      setError("URL is required");
      return;
    }
    if (draft.kind === "pagerduty" && !draft.integration_key.trim()) {
      setError("PagerDuty integrations need an integration_key (routing key)");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        kind: draft.kind,
        // teams_graph carries a target, not a URL; every other kind the
        // reverse. Sending both would make the server guess.
        ...(draft.kind === "teams_graph"
          ? {
              url: null,
              customer_id: draft.customer_id,
              target_team_id: draft.target_team_id,
              target_channel_id: draft.target_channel_id,
              target_label: draft.target_label,
            }
          : { url: draft.url.trim() }),
        integration_key: draft.integration_key.trim() || null,
        event_filter: draft.event_filter,
        enabled: draft.enabled,
      };
      if (editing) await api.put(`/api/v1/webhooks/${editing.id}`, body);
      else await api.post("/api/v1/webhooks", body);
      close();
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w: Webhook) => {
    if (!window.confirm(`Delete webhook "${w.name}"?`)) return;
    await api.delete(`/api/v1/webhooks/${w.id}`);
    load();
  };

  const test = async (w: Webhook) => {
    setTestResults((s) => ({ ...s, [w.id]: "testing…" }));
    try {
      const r = await api.post(`/api/v1/webhooks/${w.id}/test`);
      if (r.data.success) {
        setTestResults((s) => ({ ...s, [w.id]: "✓ delivered" }));
      } else {
        setTestResults((s) => ({ ...s, [w.id]: `✗ ${r.data.error || "failed"}` }));
      }
    } catch (err: any) {
      setTestResults((s) => ({
        ...s,
        [w.id]: `✗ ${err.response?.data?.detail || "request failed"}`,
      }));
    }
    // Reload to pick up updated last_success_at / last_failure_at.
    load();
    setTimeout(() => setTestResults((s) => ({ ...s, [w.id]: "" })), 8000);
  };

  /** Send a test to a URL that has not been saved yet.
   *
   * The only test used to be on a saved row, so the sequence was: create it,
   * close the dialog, find it in the table, press Test, and if it failed go
   * back to the vendor for the right URL. Testing here means the operator
   * finds out while the page they copied the URL from is still open. */
  useEffect(() => {
    if (draft.kind !== "teams_graph" || !draft.customer_id) {
      setTeams([]);
      setChannels([]);
        return;
    }
    setLoadingTeams(true);
    setGraphErr("");
    api
      .get(`/api/v1/webhooks/teams/${draft.customer_id}/teams`)
      .then((r) => {
        setTeams(r.data?.teams || []);
        if (!r.data?.ok) setGraphErr(r.data?.error || "Could not read Microsoft 365.");
      })
      .catch((e) => setGraphErr(e?.response?.data?.detail || "Could not read Microsoft 365."))
      .finally(() => setLoadingTeams(false));
  }, [draft.kind, draft.customer_id]);

  useEffect(() => {
    if (draft.kind !== "teams_graph" || !draft.customer_id || !draft.target_team_id) {
      setChannels([]);
      return;
    }
    api
      .get(
        `/api/v1/webhooks/teams/${draft.customer_id}/teams/${encodeURIComponent(
          draft.target_team_id,
        )}/channels`,
      )
      .then((r) => setChannels(r.data?.channels || []))
      .catch(() => setChannels([]));
  }, [draft.kind, draft.customer_id, draft.target_team_id]);

  /* The manual "download the Teams app package and sideload it" path lived
     here and is gone on purpose. Publishing + installing the app is something
     Graph does app-only, so "Let AeroGauge post in this team" below does the
     whole job in one call - and in a tenant that forbids custom app uploads
     (the case the manual route was meant to rescue) uploading is the very
     thing that is blocked, so it rescued nothing. The endpoint still exists
     for support to drive by hand. */
  const [enabling, setEnabling] = useState(false);
  const [enableMsg, setEnableMsg] = useState("");
  const [enableOk, setEnableOk] = useState(false);

  /** Publish + install + consent, in one call. */
  const enableTeam = async () => {
    if (!draft.customer_id || !draft.target_team_id) return;
    setEnabling(true);
    setEnableMsg("");
    try {
      const r = await api.post("/api/v1/webhooks/teams/enable", {
        customer_id: draft.customer_id,
        team_id: draft.target_team_id,
      });
      setEnableOk(true);
      setEnableMsg(r.data?.message || "Done - send a test.");
    } catch (e: any) {
      setEnableOk(false);
      setEnableMsg(e?.response?.data?.detail || "Could not set that up.");
    } finally {
      setEnabling(false);
    }
  };

  const sendTry = async () => {
    setTryState({ busy: true, result: "", ok: false });
    try {
      // teams_graph has no URL to test - it posts through the customer's
      // Microsoft 365, so the probe needs the target instead.
      const r =
        draft.kind === "teams_graph"
          ? await api.post("/api/v1/webhooks/teams/try", {
              customer_id: draft.customer_id,
              team_id: draft.target_team_id,
              channel_id: draft.target_channel_id,
            })
          : await api.post("/api/v1/webhooks/try", {
              kind: draft.kind,
              url: draft.url.trim(),
              integration_key: draft.integration_key.trim() || null,
            });
      setTryState(
        r.data.success
          ? { busy: false, ok: true, result: "Delivered — go and look at the channel." }
          : { busy: false, ok: false, result: r.data.error || "Delivery failed." },
      );
    } catch (err: any) {
      setTryState({
        busy: false,
        ok: false,
        result: err.response?.data?.detail || "Could not send the test.",
      });
    }
  };

  const toggleEvent = (t: string) => {
    setDraft({
      ...draft,
      event_filter: draft.event_filter.includes(t)
        ? draft.event_filter.filter((x) => x !== t)
        : [...draft.event_filter, t],
    });
  };

  // Keyed on `picked`, NOT on draft.kind: the draft defaults to slack, so
  // falling back to it made the picker announce "Send alerts to Slack"
  // before anything had been chosen.
  const dest = picked ? destinations.find((d) => d.key === picked) || null : null;
  // Mirror of the server's shape check, so a wrong paste is called out as the
  // operator types rather than after a save. The server still validates -
  // this is only the faster half of the same rule.
  // The same rules the server enforces, run here so a wrong paste is named
  // as you type instead of after a save. The RULES come from the server
  // (/destinations) rather than being restated in this file - the console
  // must not be able to be friendlier, or stricter, than the real check.
  const urlProblem = (() => {
    const url = draft.url.trim();
    if (!picked || !url) return "";
    // A masked URL is not a bad paste. The server hides the stored value from
    // anyone who is not an admin - it is a bearer credential - and hands back
    // this placeholder instead. Running the shape rules over it would tell a
    // technician their own saved destination is malformed.
    if (isMaskedUrl(url)) return "";
    if (!/^https:\/\//i.test(url)) return "The URL must start with https://";

    // THE SERVER'S host list, not a copy. There used to be a table right
    // here mirroring url_hosts, and it drifted the moment a second Power
    // Automate host was added - the form then rejected a correct URL and a
    // signature rule told the operator to switch to the destination they were
    // already on. A mirrored rule is a rule that will disagree.
    const hosts = dest?.url_hosts || [];
    const hostOk = !dest || hosts.length === 0 || hosts.some((h) => url.toLowerCase().includes(h));

    if (!hostOk) {
      // Say what they DO have. "Not the right URL" leaves them where they
      // were; "that is the Slack app settings page" is the next step.
      for (const s of signatures) {
        try {
          if (!new RegExp(s.pattern, "i").test(url)) continue;
          // "switch to Microsoft Teams" is nonsense when Microsoft Teams is
          // what they picked. A rule that points at the CURRENT destination
          // means the URL is right and something else is wrong, so say
          // nothing rather than send them in a circle.
          if (s.belongs_to && s.belongs_to === draft.kind) continue;
          return s.message;
        } catch {
          /* a rule that will not compile must not break the form */
        }
      }
      return `That does not look like ${dest ? dest.url_shape : "the right URL"}.`;
    }

    const missing = (dest?.url_must_contain || []).filter((m) => !url.includes(m));
    if (missing.length) {
      return "That URL is cut short — it is missing its query string. Copy the whole thing, including everything after the '?'.";
    }
    return "";
  })();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Alert destinations
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Send alerts and SLA breaches to a Slack or Teams channel, page someone through
            PagerDuty, or POST them to your own endpoint.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
        >
          + Add a destination
        </button>
      </div>

      {webhooks.length === 0 ? (
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-4">
          Nothing set up yet, so alerts go out by email only. Add a destination to get them in Slack or Teams as well.
        </p>
      ) : (
        <table className="w-full text-sm mt-4">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Name" sortKey="name" sort={whSort.sort} onSort={whSort.toggle} />
              <SortableTh label="Kind" sortKey="kind" sort={whSort.sort} onSort={whSort.toggle} />
              <SortableTh label="Events" sortKey="events" sort={whSort.sort} onSort={whSort.toggle} />
              <th className="text-left py-2 pr-3">Last status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortRows(webhooks, whSort.sort, (w, key) => {
              switch (key) {
                case "kind": return (w.kind || "").toLowerCase();
                case "events": return (w.event_filter?.length) || 0;
                default: return (w.name || "").toLowerCase();
              }
            }).map((w) => (
              <tr key={w.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                <td className="py-2 pr-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    {w.name}
                    {!w.enabled && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded">
                        disabled
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                    {isMaskedUrl(w.url || "") ? (
                      // Printing the placeholder URL here would look like a
                      // misconfigured destination pointing at a dead host.
                      <span className="italic">URL hidden - administrators only</span>
                    ) : (
                      w.url
                    )}
                  </div>
                </td>
                <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{KIND_LABELS[w.kind]}</td>
                <td className="py-2 pr-3 text-xs text-gray-600 dark:text-gray-400">
                  {w.event_filter.length === 0 ? (
                    <span className="text-amber-600 dark:text-amber-400">no events</span>
                  ) : (
                    w.event_filter.length + " events"
                  )}
                </td>
                <td className="py-2 pr-3 text-xs">
                  {testResults[w.id] ? (
                    <span
                      className={
                        testResults[w.id].startsWith("✓") ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
                      }
                    >
                      {testResults[w.id]}
                    </span>
                  ) : w.last_failure_at &&
                    (!w.last_success_at || w.last_failure_at > w.last_success_at) ? (
                    <span className="text-red-700 dark:text-red-400">
                      ✗ {formatRelative(w.last_failure_at)}
                    </span>
                  ) : w.last_success_at ? (
                    <span className="text-green-700 dark:text-green-400">
                      ✓ {formatRelative(w.last_success_at)}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">never delivered</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => test(w)}
                    className="text-blue-600 dark:text-blue-400 text-xs hover:underline mr-3"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => openEdit(w)}
                    className="text-gray-600 dark:text-gray-300 text-xs hover:underline mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(w)}
                    className="text-red-600 dark:text-red-400 text-xs hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editing
                  ? `Edit ${editing.name}`
                  : dest
                    ? `Send alerts to ${dest.label}`
                    : "Where should alerts go?"}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {dest
                  ? dest.blurb
                  : "Pick a destination and AeroGauge will walk you through setting it up."}
              </p>
            </div>

            {/* 1. pick a destination */}
            {!picked && (
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {destinations.map((d) => (
                  <button
                    key={d.key}
                    onClick={() => {
                      setPicked(d.key);
                      setDraft({
                        ...EMPTY_DRAFT,
                        // The page already has a customer selected in the
                        // switcher at the top. Asking again is the thing the
                        // cross-cutting rule exists to stop.
                        customer_id: currentCustomerId ?? null,
                        kind: d.key as WebhookKind,
                        // PagerDuty's endpoint is the same for everyone, so
                        // there is nothing to go and look up.
                        url: d.key === "pagerduty" ? d.url_placeholder : "",
                        name: d.label,
                      });
                      setTryState({ busy: false, result: "", ok: false });
                      setError("");
                    }}
                    className="text-left border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 hover:border-brand hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {d.label}
                      </span>
                      {d.recommended && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                          easiest
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.blurb}</div>
                  </button>
                ))}
              </div>
            )}

            {/* 2. the vendor's own steps, then the URL they produce */}
            {picked && (
              <div className="px-6 py-5 space-y-5">
                {dest && !editing && (
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 px-4 py-3">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">
                      {dest.no_url
                        ? "How this works"
                        : `Do this in ${dest.label.replace(/\s*\(.*\)$/, "")} first`}
                    </div>
                    {dest.open_links.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {dest.open_links.map((l) => (
                          <a
                            key={l.url}
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-2.5 py-1 text-xs rounded-lg bg-brand text-white hover:opacity-90"
                          >
                            {l.label} ↗
                          </a>
                        ))}
                      </div>
                    )}
                    <ol className="space-y-1.5">
                      {dest.steps.map((s, i) => (
                        <li key={i} className="text-xs text-gray-600 dark:text-gray-300 flex gap-2">
                          <span className="shrink-0 w-4 h-4 mt-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] flex items-center justify-center text-gray-700 dark:text-gray-200">
                            {i + 1}
                          </span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ol>
                    {dest.notes.map((n, i) => (
                      <p key={i} className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                        {n}
                      </p>
                    ))}
                    {dest.fallback_steps.length > 0 && (
                      <details className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-2">
                        <summary className="text-xs text-brand dark:text-brand-light cursor-pointer">
                          {dest.fallback_title}
                        </summary>
                        <ol className="mt-2 space-y-1.5">
                          {dest.fallback_steps.map((s, i) => (
                            <li
                              key={i}
                              className="text-xs text-gray-600 dark:text-gray-300 flex gap-2"
                            >
                              <span className="shrink-0 w-4 h-4 mt-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] flex items-center justify-center text-gray-700 dark:text-gray-200">
                                {i + 1}
                              </span>
                              <span className="break-words">{s}</span>
                            </li>
                          ))}
                        </ol>
                      </details>
                    )}
                    {dest.docs_url && (
                      <a
                        href={dest.docs_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-[11px] text-brand dark:text-brand-light hover:underline"
                      >
                        {dest.label} documentation
                      </a>
                    )}
                  </div>
                )}

                {dest && dest.no_url ? (
                  <>
                    {/* No URL. Pick where it goes. */}
                    <div>
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                        Customer
                      </label>
                      <select
                        value={draft.customer_id ?? ""}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            customer_id: e.target.value ? Number(e.target.value) : null,
                            target_team_id: "",
                            target_channel_id: "",
                            target_label: "",
                          })
                        }
                        className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                      >
                        <option value="">Choose a customer…</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                        {currentCustomerId && draft.customer_id === currentCustomerId
                          ? "Taken from the customer selected at the top of the page. Change it here if you meant a different one."
                          : "Whose Microsoft 365 connection AeroGauge posts through."}
                      </p>
                    </div>

                    {graphErr && (
                      <p className="text-xs text-red-600 dark:text-red-400">{graphErr}</p>
                    )}
                    {/* Download IS the setup. Posting into a channel cannot be
                        consented in Entra - it is granted per team by installing
                        this package, so the button is step one rather than a
                        remedy for a missing permission. */}
                    {/* ONE BUTTON. Publishing and installing the Teams app is
                        something Graph can do app-only, so there is nothing for
                        the operator to download or upload - which matters,
                        because plenty of tenants forbid custom app uploads and
                        the menu item is simply absent. */}
                    {draft.customer_id && draft.target_team_id && (
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            onClick={enableTeam}
                            disabled={enabling}
                            className="px-3 py-1.5 text-xs rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
                          >
                            {enabling ? "Setting up…" : "Let AeroGauge post in this team"}
                          </button>
                          {enableMsg && (
                            <span
                              className={
                                enableOk
                                  ? "text-xs text-green-700 dark:text-green-400"
                                  : "text-xs text-red-700 dark:text-red-400"
                              }
                            >
                              {enableMsg}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                          Adds AeroGauge to that team and grants it permission to post there,
                          and nowhere else. Nothing to download, nothing to upload.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                          Team
                        </label>
                        <select
                          value={draft.target_team_id || ""}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              target_team_id: e.target.value,
                              target_channel_id: "",
                            })
                          }
                          disabled={!draft.customer_id || loadingTeams}
                          className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 disabled:opacity-50"
                        >
                          <option value="">
                            {loadingTeams ? "Loading…" : "Choose a team…"}
                          </option>
                          {teams.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                          Channel
                        </label>
                        <select
                          value={draft.target_channel_id || ""}
                          onChange={(e) => {
                            const team = teams.find((t) => t.id === draft.target_team_id);
                            const chan = channels.find((c) => c.id === e.target.value);
                            setDraft({
                              ...draft,
                              target_channel_id: e.target.value,
                              target_label:
                                team && chan ? `${team.name} / ${chan.name}` : "",
                              name:
                                draft.name && draft.name !== "Microsoft Teams (recommended)"
                                  ? draft.name
                                  : chan
                                    ? `Teams — ${chan.name}`
                                    : draft.name,
                            });
                          }}
                          disabled={!draft.target_team_id}
                          className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 disabled:opacity-50"
                        >
                          <option value="">Choose a channel…</option>
                          {channels.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                <div>
                  <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    {dest && dest.key === "pagerduty" ? "Events API endpoint" : "Paste the URL here"}
                  </label>
                  <input
                    value={isMaskedUrl(draft.url) ? "" : draft.url}
                    onChange={(e) => {
                      setDraft({ ...draft, url: e.target.value });
                      setTryState({ busy: false, result: "", ok: false });
                    }}
                    placeholder={
                      isMaskedUrl(draft.url)
                        ? "Saved and hidden - paste a new URL to replace it"
                        : dest
                          ? dest.url_placeholder
                          : "https://"
                    }
                    className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-xs mt-1 font-mono"
                  />
                  {isMaskedUrl(draft.url) && (
                    // Say WHY it is empty. A blank box on a destination that
                    // demonstrably works reads as data loss, and the next move
                    // would be to go and fetch the URL from the vendor again.
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      This URL is stored and working. It is hidden because it acts as a password
                      for the channel - only an administrator can read it back. Leave this blank to
                      keep it.
                    </p>
                  )}
                  {urlProblem && (
                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                      {urlProblem}
                    </p>
                  )}
                </div>

                {dest && dest.needs_key && (
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      {dest.key_label}
                    </label>
                    <input
                      value={draft.integration_key}
                      onChange={(e) => setDraft({ ...draft, integration_key: e.target.value })}
                      className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 font-mono"
                    />
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                      {dest.key_help}
                    </p>
                  </div>
                )}
                  </>
                )}

                {/* 3. prove it works BEFORE saving */}
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={sendTry}
                      disabled={
                        tryState.busy ||
                        (dest?.no_url
                          ? !draft.customer_id || !draft.target_channel_id
                          : // A masked URL cannot be tested from here: this
                            // button posts the URL in the box, and the box
                            // does not have the real one. Save, then use Test
                            // on the row - that fires through the stored URL.
                            isMaskedUrl(draft.url) || !!urlProblem || !draft.url.trim())
                      }
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                    >
                      {tryState.busy ? "Sending..." : "Send a test message"}
                    </button>
                    {isMaskedUrl(draft.url) && (
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Save first, then use Test on the row to send through the stored URL.
                      </span>
                    )}
                    {tryState.result && (
                      <span
                        className={
                          tryState.ok
                            ? "text-xs text-green-700 dark:text-green-400"
                            : "text-xs text-red-700 dark:text-red-400"
                        }
                      >
                        {tryState.result}
                      </span>
                    )}
                  </div>
                  {dest && dest.sample && (
                    <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                      What arrives: {dest.sample}
                    </p>
                  )}
                </div>

                {/* 4. name it, and choose what it hears about */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Name
                    </label>
                    <input
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="e.g. #ops channel"
                      className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                      Only for your own reference in this list.
                    </p>
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Enabled
                    </label>
                    <select
                      value={draft.enabled ? "yes" : "no"}
                      onChange={(e) => setDraft({ ...draft, enabled: e.target.value === "yes" })}
                      className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No (disabled)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-2">
                    Send it these
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {WEBHOOK_EVENT_TYPES.map((t) => (
                      <label
                        key={t}
                        className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={draft.event_filter.includes(t)}
                          onChange={() => toggleEvent(t)}
                        />
                        {EVENT_LABELS[t]}
                      </label>
                    ))}
                  </div>
                  {draft.event_filter.length === 0 && (
                    <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                      Nothing selected, so this destination would stay silent.
                    </p>
                  )}
                </div>

                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded">
                    {error}
                  </p>
                )}
              </div>
            )}

            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-between gap-3">
              <button
                onClick={() => (picked && !editing ? setPicked(null) : close())}
                className="border dark:border-gray-600 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
              >
                {picked && !editing ? "Back" : "Cancel"}
              </button>
              {picked && (
                <button
                  onClick={save}
                  disabled={
                    saving ||
                    (dest?.no_url
                      ? !draft.customer_id || !draft.target_channel_id
                      : !!urlProblem || !draft.url.trim())
                  }
                  className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                >
                  {saving ? "Saving..." : editing ? "Save changes" : "Save destination"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
